import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Loader2, Save, Plus, Trash2, ArrowLeft, Upload, Check, Users, BarChart3,
  Settings2, Search, FileText, CheckCircle2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { academyAPI, agentAPI, agencyAPI } from '@/lib/api';
import { getErrorMessage } from '@/lib/utils';

const uid = () => (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2));
const ROLES = ['Agent', 'Agency_Principal', 'Agency_User', 'Accounting', 'KR_AdminOps'];
const PRODUCTS = ['Medicare', 'ACA / Health', 'Life', 'Annuity', 'Final Expense', 'Dental / Vision'];
const TRACK_STATUS = {
  completed: 'bg-emerald-100 text-emerald-700', in_progress: 'bg-teal-100 text-teal-700',
  not_started: 'bg-slate-100 text-slate-600', failed: 'bg-rose-100 text-rose-700', expired: 'bg-amber-100 text-amber-800',
};

function toggle(arr, v) { const s = new Set(arr || []); if (s.has(v)) s.delete(v); else s.add(v); return Array.from(s); }

function CheckList({ items, selected, onToggle, labelKey, valueKey, testPrefix }) {
  return (
    <div className="max-h-44 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100" data-testid={`checklist-${testPrefix}`}>
      {items.length === 0 ? <div className="p-3 text-xs text-slate-400">None available</div> : items.map((it) => {
        const val = it[valueKey]; const on = (selected || []).includes(val);
        return (
          <button key={val} type="button" onClick={() => onToggle(val)} className={`w-full flex items-center gap-2 px-3 py-1.5 text-left text-xs ${on ? 'bg-teal-50' : 'hover:bg-slate-50'}`} data-testid={`${testPrefix}-${val}`}>
            <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${on ? 'bg-teal-600 border-teal-600 text-white' : 'border-slate-300'}`}>{on && <Check size={11} />}</span>
            <span className="truncate">{it[labelKey]}</span>
          </button>
        );
      })}
    </div>
  );
}

export default function CourseBuilderPage() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [agents, setAgents] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agentSearch, setAgentSearch] = useState('');
  const [track, setTrack] = useState(null);
  const [trackLoading, setTrackLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, ag, agc] = await Promise.all([
        academyAPI.getCourse(courseId),
        agentAPI.list().catch(() => ({ data: [] })),
        agencyAPI.list().catch(() => ({ data: [] })),
      ]);
      setC(r.data.course);
      setAgents((ag.data || []).map((a) => ({ npn: a.npn, name: `${a.first_name || ''} ${a.last_name || ''}`.trim() || a.npn })));
      setAgencies((agc.data || []).map((a) => ({ agency_id: a.agency_id, agency_name: a.agency_name })));
    } catch (e) { toast.error(getErrorMessage(e, 'Could not load course')); }
    finally { setLoading(false); }
  }, [courseId]);
  useEffect(() => { load(); }, [load]);

  const loadTrack = useCallback(async () => {
    setTrackLoading(true);
    try { const r = await academyAPI.courseProgress(courseId); setTrack(r.data); }
    catch (e) { toast.error(getErrorMessage(e, 'Could not load tracking')); }
    finally { setTrackLoading(false); }
  }, [courseId]);

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));

  const save = async (extra = {}) => {
    setSaving(true);
    try {
      const payload = {
        title: c.title, description: c.description, category: c.category || '',
        required: !!c.required, due_date: c.due_date || null,
        assign_all: c.assign_all !== false,
        assign_agent_npns: c.assign_agent_npns || [], assign_agency_ids: c.assign_agency_ids || [],
        assign_roles: c.assign_roles || [], assign_products: c.assign_products || [],
        exam_mode: c.exam_mode, pass_score: c.pass_score, max_attempts: c.max_attempts,
        gives_certificate: c.gives_certificate, modules: c.modules,
        final_exam: c.final_exam || { questions: [] }, ...extra,
      };
      const r = await academyAPI.updateCourse(courseId, payload);
      setC(r.data);
      if ('status' in extra) toast.success(extra.status === 'published' ? 'Course published' : 'Course unpublished');
      else toast.success('Course saved');
    } catch (e) { toast.error(getErrorMessage(e, 'Could not save')); }
    finally { setSaving(false); }
  };

  if (loading || !c) return <div className="flex items-center justify-center min-h-[40vh]"><Loader2 className="animate-spin text-slate-400" /></div>;

  const perModule = c.exam_mode === 'per_module';
  const assignAll = c.assign_all !== false;
  const addModule = () => set('modules', [...(c.modules || []), { module_id: uid(), title: 'New module', lessons: [], quiz: { questions: [] } }]);
  const updateModule = (mid, patch) => set('modules', c.modules.map((m) => m.module_id === mid ? { ...m, ...patch } : m));
  const removeModule = (mid) => set('modules', c.modules.filter((m) => m.module_id !== mid));
  const filteredAgents = agentSearch.trim() ? agents.filter((a) => a.name.toLowerCase().includes(agentSearch.toLowerCase()) || a.npn.includes(agentSearch)) : agents.slice(0, 40);

  return (
    <div data-testid="course-builder-page">
      <div className="page-content space-y-5 max-w-4xl">
        <div className="flex items-center justify-between">
          <button onClick={() => navigate('/academy')} className="text-sm text-slate-500 hover:text-slate-800 flex items-center gap-1"><ArrowLeft size={15} /> Academy</button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => save()} disabled={saving} data-testid="course-save-btn">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save</Button>
            <Button onClick={() => save({ status: c.status === 'published' ? 'draft' : 'published' })} disabled={saving} data-testid="course-publish-btn">
              {c.status === 'published' ? 'Unpublish' : <><Upload size={14} className="mr-1" /> Publish</>}
            </Button>
          </div>
        </div>

        <Tabs defaultValue="build" onValueChange={(v) => { if (v === 'tracking') loadTrack(); }}>
          <TabsList data-testid="builder-tabs">
            <TabsTrigger value="build" className="gap-1.5" data-testid="builder-tab-build"><Settings2 size={14} /> Build</TabsTrigger>
            <TabsTrigger value="assign" className="gap-1.5" data-testid="builder-tab-assign"><Users size={14} /> Assignment</TabsTrigger>
            <TabsTrigger value="tracking" className="gap-1.5" data-testid="builder-tab-tracking"><BarChart3 size={14} /> Tracking</TabsTrigger>
          </TabsList>

          {/* BUILD */}
          <TabsContent value="build" className="space-y-5 mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Course details</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div><Label className="text-xs">Title</Label><Input value={c.title} onChange={(e) => set('title', e.target.value)} data-testid="course-title" /></div>
                <div><Label className="text-xs">Description</Label><Textarea rows={2} value={c.description} onChange={(e) => set('description', e.target.value)} data-testid="course-description" /></div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div><Label className="text-xs">Category</Label><Input value={c.category || ''} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Medicare" data-testid="course-category" /></div>
                  <div><Label className="text-xs">Due date</Label><Input type="date" value={(c.due_date || '').slice(0, 10)} onChange={(e) => set('due_date', e.target.value || null)} data-testid="course-due-date" /></div>
                  <div className="flex items-center gap-2 pt-5"><Switch checked={!!c.required} onCheckedChange={(v) => set('required', v)} data-testid="course-required" /><span className="text-sm">Required course</span></div>
                </div>
                <div className="grid sm:grid-cols-4 gap-3 border-t pt-4">
                  <div>
                    <Label className="text-xs">Exam mode</Label>
                    <Select value={c.exam_mode} onValueChange={(v) => set('exam_mode', v)}>
                      <SelectTrigger data-testid="course-exam-mode"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="final">Final exam only</SelectItem><SelectItem value="per_module">Quiz per module</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div><Label className="text-xs">Pass score %</Label><Input type="number" value={c.pass_score} onChange={(e) => set('pass_score', Number(e.target.value))} data-testid="course-pass-score" /></div>
                  <div><Label className="text-xs">Max attempts (0=∞)</Label><Input type="number" value={c.max_attempts} onChange={(e) => set('max_attempts', Number(e.target.value))} data-testid="course-max-attempts" /></div>
                  <div className="flex items-center gap-2 pt-5"><Switch checked={c.gives_certificate} onCheckedChange={(v) => set('gives_certificate', v)} data-testid="course-gives-cert" /><span className="text-sm">Certificate</span></div>
                </div>
              </CardContent>
            </Card>

            {(c.modules || []).map((m, mi) => (
              <Card key={m.module_id} data-testid={`module-${m.module_id}`}>
                <CardHeader className="flex flex-row items-center justify-between">
                  <Input value={m.title} onChange={(e) => updateModule(m.module_id, { title: e.target.value })} className="max-w-sm font-semibold" data-testid={`module-title-${mi}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeModule(m.module_id)} className="text-rose-600" data-testid={`module-remove-${mi}`}><Trash2 size={15} /></Button>
                </CardHeader>
                <CardContent className="space-y-3">
                  {(m.lessons || []).map((l, li) => (
                    <div key={l.lesson_id} className="border border-slate-100 rounded-lg p-3 space-y-2" data-testid={`lesson-${l.lesson_id}`}>
                      <div className="flex items-center gap-2">
                        <Input value={l.title} onChange={(e) => updateModule(m.module_id, { lessons: m.lessons.map((x) => x.lesson_id === l.lesson_id ? { ...x, title: e.target.value } : x) })} placeholder="Lesson title" className="flex-1" data-testid={`lesson-title-${mi}-${li}`} />
                        <Button size="sm" variant="ghost" onClick={() => updateModule(m.module_id, { lessons: m.lessons.filter((x) => x.lesson_id !== l.lesson_id) })} className="text-rose-600"><Trash2 size={14} /></Button>
                      </div>
                      <Input value={l.video_url || ''} onChange={(e) => updateModule(m.module_id, { lessons: m.lessons.map((x) => x.lesson_id === l.lesson_id ? { ...x, video_url: e.target.value } : x) })} placeholder="YouTube URL" data-testid={`lesson-video-${mi}-${li}`} />
                      <Textarea rows={2} value={l.content || ''} onChange={(e) => updateModule(m.module_id, { lessons: m.lessons.map((x) => x.lesson_id === l.lesson_id ? { ...x, content: e.target.value } : x) })} placeholder="Written material / notes" data-testid={`lesson-content-${mi}-${li}`} />
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => updateModule(m.module_id, { lessons: [...(m.lessons || []), { lesson_id: uid(), title: 'New lesson', video_url: '', content: '' }] })} data-testid={`lesson-add-${mi}`}><Plus size={14} className="mr-1" /> Add lesson</Button>
                  {perModule && (
                    <QuestionEditor label="Module quiz" questions={m.quiz?.questions || []} onChange={(qs) => updateModule(m.module_id, { quiz: { questions: qs } })} testPrefix={`mod-${mi}`} />
                  )}
                </CardContent>
              </Card>
            ))}
            <Button variant="outline" onClick={addModule} data-testid="module-add"><Plus size={15} className="mr-1" /> Add module</Button>

            {!perModule && (
              <Card>
                <CardHeader><CardTitle className="text-base">Final exam</CardTitle></CardHeader>
                <CardContent>
                  <QuestionEditor label="Final exam" questions={c.final_exam?.questions || []} onChange={(qs) => set('final_exam', { questions: qs })} testPrefix="final" />
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* ASSIGNMENT */}
          <TabsContent value="assign" className="mt-4">
            <Card>
              <CardHeader><CardTitle className="text-base">Who takes this course?</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-3 rounded-lg border border-slate-200 p-3">
                  <Switch checked={assignAll} onCheckedChange={(v) => set('assign_all', v)} data-testid="assign-all-switch" />
                  <div><div className="text-sm font-medium text-slate-800">Assign to everyone</div><div className="text-xs text-slate-500">All agents and staff see this course.</div></div>
                </div>
                {!assignAll && (
                  <div className="grid sm:grid-cols-2 gap-4" data-testid="assign-targeting">
                    <div>
                      <Label className="text-xs mb-1 block">Roles</Label>
                      <CheckList testPrefix="assign-role" items={ROLES.map((r) => ({ v: r, l: r.replace(/_/g, ' ') }))} selected={c.assign_roles} onToggle={(v) => set('assign_roles', toggle(c.assign_roles, v))} labelKey="l" valueKey="v" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Product groups</Label>
                      <CheckList testPrefix="assign-product" items={PRODUCTS.map((p) => ({ v: p, l: p }))} selected={c.assign_products} onToggle={(v) => set('assign_products', toggle(c.assign_products, v))} labelKey="l" valueKey="v" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Agencies</Label>
                      <CheckList testPrefix="assign-agency" items={agencies} selected={c.assign_agency_ids} onToggle={(v) => set('assign_agency_ids', toggle(c.assign_agency_ids, v))} labelKey="agency_name" valueKey="agency_id" />
                    </div>
                    <div>
                      <Label className="text-xs mb-1 block">Agents {(c.assign_agent_npns || []).length > 0 && <span className="text-teal-600">({c.assign_agent_npns.length})</span>}</Label>
                      <div className="relative mb-1">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <Input value={agentSearch} onChange={(e) => setAgentSearch(e.target.value)} placeholder="Search agents" className="pl-8 h-8" data-testid="assign-agent-search" />
                      </div>
                      <CheckList testPrefix="assign-agent" items={filteredAgents} selected={c.assign_agent_npns} onToggle={(v) => set('assign_agent_npns', toggle(c.assign_agent_npns, v))} labelKey="name" valueKey="npn" />
                    </div>
                  </div>
                )}
                <Button onClick={() => save()} disabled={saving} data-testid="assign-save-btn">{saving ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Save size={14} className="mr-1" />} Save assignment</Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TRACKING */}
          <TabsContent value="tracking" className="mt-4">
            {trackLoading ? (
              <div className="flex items-center justify-center py-14"><Loader2 className="animate-spin text-slate-400" /></div>
            ) : !track ? (
              <Card><CardContent className="py-10 text-center text-sm text-slate-500">No tracking data.</CardContent></Card>
            ) : (
              <Card>
                <CardContent className="p-4 space-y-4">
                  <div className="flex flex-wrap gap-2" data-testid="tracking-summary">
                    <Badge className="bg-slate-100 text-slate-700">Assigned {track.summary.total}</Badge>
                    <Badge className="bg-emerald-100 text-emerald-700">Completed {track.summary.completed}</Badge>
                    <Badge className="bg-teal-100 text-teal-700">In progress {track.summary.in_progress}</Badge>
                    <Badge className="bg-slate-100 text-slate-600">Not started {track.summary.not_started}</Badge>
                    {track.summary.failed > 0 && <Badge className="bg-rose-100 text-rose-700">Failed {track.summary.failed}</Badge>}
                    {track.summary.expired > 0 && <Badge className="bg-amber-100 text-amber-800">Expired {track.summary.expired}</Badge>}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tracking-table">
                      <thead><tr className="text-left text-xs text-slate-500 border-b">
                        <th className="py-2 pr-3">Name</th><th className="py-2 pr-3">NPN</th><th className="py-2 pr-3">Status</th>
                        <th className="py-2 pr-3">Progress</th><th className="py-2 pr-3">Score</th><th className="py-2 pr-3">Certificate</th>
                      </tr></thead>
                      <tbody>
                        {track.rows.length === 0 ? (
                          <tr><td colSpan={6} className="py-8 text-center text-slate-400 text-xs">No one is assigned to this course yet.</td></tr>
                        ) : track.rows.map((r) => (
                          <tr key={r.user_id} className="border-b border-slate-50" data-testid={`tracking-row-${r.user_id}`}>
                            <td className="py-2 pr-3 font-medium text-slate-800">{r.name}</td>
                            <td className="py-2 pr-3 text-slate-500">{r.npn || '—'}</td>
                            <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${TRACK_STATUS[r.status] || 'bg-slate-100 text-slate-600'}`}>{r.status.replace(/_/g, ' ')}</span></td>
                            <td className="py-2 pr-3 text-slate-600">{r.progress_pct}%</td>
                            <td className="py-2 pr-3 text-slate-600">{r.best_score ? `${r.best_score}%` : '—'}</td>
                            <td className="py-2 pr-3">{r.certificate_id ? (
                              <button onClick={() => navigate(`/academy/certificate/${r.certificate_id}`)} className="text-teal-600 hover:underline inline-flex items-center gap-1 text-xs" data-testid={`tracking-cert-${r.user_id}`}><FileText size={12} /> View</button>
                            ) : <span className="text-slate-300">—</span>}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function QuestionEditor({ questions, onChange, testPrefix }) {
  const addQ = (type) => onChange([...(questions || []), type === 'tf'
    ? { question_id: uid(), type: 'tf', prompt: '', correct_bool: true }
    : { question_id: uid(), type: 'mc', prompt: '', options: [{ id: uid(), text: '' }, { id: uid(), text: '' }], correct_option_ids: [] }]);
  const upd = (qid, patch) => onChange(questions.map((q) => q.question_id === qid ? { ...q, ...patch } : q));
  const del = (qid) => onChange(questions.filter((q) => q.question_id !== qid));

  return (
    <div className="mt-3 border-t pt-3 space-y-3" data-testid={`qeditor-${testPrefix}`}>
      {(questions || []).map((q, qi) => (
        <div key={q.question_id} className="bg-slate-50 rounded-lg p-3 space-y-2" data-testid={`${testPrefix}-q-${qi}`}>
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-500">{q.type === 'tf' ? 'T/F' : 'Multiple'}</span>
            <Input value={q.prompt} onChange={(e) => upd(q.question_id, { prompt: e.target.value })} placeholder="Question" className="flex-1" data-testid={`${testPrefix}-q-${qi}-prompt`} />
            <Button size="sm" variant="ghost" onClick={() => del(q.question_id)} className="text-rose-600"><Trash2 size={13} /></Button>
          </div>
          {q.type === 'tf' ? (
            <div className="flex gap-2 text-sm">
              {[true, false].map((v) => (
                <button key={String(v)} onClick={() => upd(q.question_id, { correct_bool: v })} className={`px-3 py-1 rounded-md border ${q.correct_bool === v ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-200'}`} data-testid={`${testPrefix}-q-${qi}-${v ? 'true' : 'false'}`}>{v ? 'True' : 'False'} {q.correct_bool === v ? '✓' : ''}</button>
              ))}
            </div>
          ) : (
            <div className="space-y-1.5">
              {(q.options || []).map((o) => {
                const correct = (q.correct_option_ids || []).includes(o.id);
                return (
                  <div key={o.id} className="flex items-center gap-2">
                    <button onClick={() => {
                      const cur = new Set(q.correct_option_ids || []);
                      if (cur.has(o.id)) cur.delete(o.id); else cur.add(o.id);
                      upd(q.question_id, { correct_option_ids: Array.from(cur) });
                    }} className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${correct ? 'bg-emerald-600 border-emerald-600 text-white' : 'border-slate-300'}`} title="Mark correct" data-testid={`${testPrefix}-q-${qi}-correct-${o.id}`}><Check size={13} /></button>
                    <Input value={o.text} onChange={(e) => upd(q.question_id, { options: q.options.map((x) => x.id === o.id ? { ...x, text: e.target.value } : x) })} placeholder="Option" className="flex-1 h-8" />
                    <Button size="sm" variant="ghost" onClick={() => upd(q.question_id, { options: q.options.filter((x) => x.id !== o.id) })} className="text-slate-400"><Trash2 size={12} /></Button>
                  </div>
                );
              })}
              <Button size="sm" variant="ghost" onClick={() => upd(q.question_id, { options: [...(q.options || []), { id: uid(), text: '' }] })}><Plus size={12} className="mr-1" /> Option</Button>
            </div>
          )}
        </div>
      ))}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => addQ('mc')} data-testid={`${testPrefix}-add-mc`}><Plus size={13} className="mr-1" /> Multiple choice</Button>
        <Button size="sm" variant="outline" onClick={() => addQ('tf')} data-testid={`${testPrefix}-add-tf`}><Plus size={13} className="mr-1" /> True/False</Button>
      </div>
    </div>
  );
}
